import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../css/dashboardPage.css';
import CareerChart from '../../../../component-controller-mod/component-serv/dashboard-fs-cp/career-dsb-op/career-chart/ts/careerChart';
import MediaChart from '../../../../component-controller-mod/component-serv/dashboard-fs-cp/media-dsb-op/media-chart/ts/mediaChart';
import InboxChart from '../../../../component-controller-mod/component-serv/dashboard-fs-cp/inbox-dsb-op/inbox-chart/ts/inboxChart';
import NotificationTable from '../../../../component-controller-mod/component-serv/dashboard-fs-cp/notification-dsb-op/notification-table/ts/notificationTable';
import WorkflowCard from '../../../../component-controller-mod/component-serv/dashboard-fs-cp/workflow-dsb-op/workflow-card/ts/workflowCard';
import {
  STAT_CARDS,
  MEDIA_FOOTER_LINK,
  INBOX_FOOTER_LINK,
  buildStatCards,
  buildBarData,
  buildMediaLegend,
  buildMediaStats,
  buildInboxLegend,
  type DashboardOverview,
} from './dashboardData';
import { useNavigate } from 'react-router-dom';
import { Confirmation } from '@core/uilibrary';
import ApiGet from '../../../../api-controller-mod/api-serv/get-api/getApi';
import ApiPost from '../../../../api-controller-mod/api-serv/post-api/postApi';
import { BriefcaseIcon, ChevronDownIcon, ImgVideoIcon,   InfoIcon,
  WarningConfirmationIcon,
  SuccessIcon,
  ErrorIcon,} from '../../../../asset-controller-mod/asset-serv/icons/iconExporter';
import UploadMediaOverlay, {
  UploadMediaFormData,
  ConfirmConfig,
} from '../../../../component-controller-mod/component-serv/texteditor-fs-cp/upload-media-overlay/ts/uploadMediaOverlay';
import { useGlobalPopupProvider } from '../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider';
import { useToast } from '../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider';
import { groupPermissionAccessCMSCareer, groupPermissionAccessCMSNotification, groupPermissionAccessInbox, groupPermissionAccessMedia, groupPermissionAccessWorkflow } from '../../../../context-controller-mod/context-serv/permission-provider/permissionExporter';
import Button from '../../../../component-controller-mod/component-serv/button-fs-cp/dynamic-button-op/ts/button';
// ─── API ──────────────────────────────────────────────────────────────────────

const getDashboardOverview = () =>
  ApiGet<{ success: boolean; data: DashboardOverview }>('/api/v1/dashboard/overview');

function getCookie(name: string): string {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : '';
}

async function uploadMediaApi(data: UploadMediaFormData): Promise<void> {
  const userUuid = getCookie('userUuid');
  const file = data.files[0];

  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', 'IMAGE');
  formData.append('filename', data.fileName);
  formData.append('alt', data.altText ?? '');
  formData.append('description', data.description ?? '');

  await ApiPost(
    `/api/v1/media/upload?userUuid=${userUuid}`,
    formData,
  );
}

// ─── Confirm Icons ────────────────────────────────────────────────────────────

const CONFIRM_ICONS: Record<ConfirmConfig['status'], React.ReactNode> = {
  info: <InfoIcon />,
  warning: <WarningConfirmationIcon />,
  success: <SuccessIcon />,
  error: <ErrorIcon />,
};

// ─── Constants ────────────────────────────────────────────────────────────────

const POLL_INTERVAL = 30 * 1000;

// ─── Page ─────────────────────────────────────────────────────────────────────

const DashboardPage = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { openPopup, closePopup, openConfirm, closeConfirm } = useGlobalPopupProvider();
  const { showToast } = useToast();

  const canCreateMedia = groupPermissionAccessMedia.create()
  const canAccessWorkflow = groupPermissionAccessWorkflow.view()
  const canAccessCareer = groupPermissionAccessCMSCareer.view()
  const canAccessMedia = groupPermissionAccessMedia.view()
  const canAccessInbox = groupPermissionAccessInbox.view()
  const canAccessNotification = groupPermissionAccessCMSNotification.view()

  // ── Fetch dashboard overview ───────────────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    try {
      const res = await getDashboardOverview();
      const data = res?.data?.data ?? (res as any)?.data;
      if (data) setOverview(data);
    } catch (e) {
      console.error('[Dashboard] overview fetch failed:', e);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    const poll = setInterval(fetchOverview, POLL_INTERVAL);
    return () => clearInterval(poll);
  }, [fetchOverview]);

  // ── Close dropdown when clicking outside ──────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Upload overlay ─────────────────────────────────────────────────────────
  const handleConfirmRequest = useCallback((config: ConfirmConfig) => {
    openConfirm(
      <Confirmation
        visible
        title={config.title}
        description={config.description}
        status={config.status}
        icon={CONFIRM_ICONS[config.status]}
        onClose={closeConfirm}
        buttons={
          <>
            <Button variant="ghost" size="md" value="Cancel" type="button"
              onClick={() => {
                closeConfirm();
                if (config.status === 'warning') {
                  showToast({ status: 'warning', title: 'Uncompleted !', description: 'Media file was updated unsuccessfully.' });
                }
              }}
            />
            <Button variant="primary" size="md" value="Confirm" type="button"
              onClick={() => {
                try {
                  config.onConfirm();
                  closeConfirm();
                  if (config.status === 'warning') {
                    showToast({ status: 'error', title: 'Uncompleted !', description: 'Media file was updated unsuccessfully.' });
                  }
                } catch {
                  closeConfirm();
                  showToast({ status: 'error', title: 'Uncompleted !', description: 'Media file was updated unsuccessfully.' });
                }
              }}
            />
          </>
        }
      />
    );
  }, [openConfirm, closeConfirm, showToast]);

  const handleOpenUpload = useCallback(() => {
    setDropdownOpen(false);
    openPopup(
      <UploadMediaOverlay
        onClose={closePopup}
        onConfirmRequest={handleConfirmRequest}
        acceptImagesOnly={true}
        onSave={async (data: UploadMediaFormData) => {
          try {
            await uploadMediaApi(data);
            showToast({
              status: 'success',
              title: 'Complete',
              description: 'Media file was uploaded successfully.',
            });
            closePopup();
            navigate('/media');
          } catch (e) {
            console.error('[Dashboard] upload failed:', e);
            showToast({
              status: 'error',
              title: 'Upload failed',
              description: 'Could not upload the file. Please try again.',
            });
          }
        }}
      />
    );
  }, [openPopup, closePopup, handleConfirmRequest, showToast]);

  // ── Derive chart data from overview via factory functions ──────────────────
  const statCards = overview ? buildStatCards(overview) : STAT_CARDS;
  const barData = overview ? buildBarData(overview) : undefined;
  const mediaLegend = overview ? buildMediaLegend(overview) : undefined;
  const mediaStats  = overview ? buildMediaStats(overview)  : undefined;
  const inboxLegend = overview ? buildInboxLegend(overview) : undefined;

  return (
    <div className="scl--cms-pages-dashboard">

      {/* ── Header ── */}
      <div className="scl--cms-pages-dashboard-header">
        <div className="scl--cms-pages-dashboard-header-content">
          <h1 className="scl--cms-pages-dashboard-title">Dashboard</h1>
          <p className="scl--cms-pages-dashboard-description">
            Here's a summary of your content management system.
          </p>
        </div>

        {canCreateMedia &&
          <div className="scl--cms-pages-dashboard-add-wrapper" ref={dropdownRef}>
            <Button
              variant="primary"
              value=" Add new"
              iconPosition='right'
              icon={<ChevronDownIcon />}
              onClick={() => setDropdownOpen((prev) => !prev)}
            />
            {dropdownOpen && (
              <div className="scl--cms-pages-dashboard-dropdown">
                <button
                  className="scl--cms-pages-dashboard-dropdown-item"
                  onClick={handleOpenUpload}
                >
                  <ImgVideoIcon />
                  <span>Media</span>
                </button>
                <button
                  className="scl--cms-pages-dashboard-dropdown-item"
                  onClick={() => { setDropdownOpen(false); navigate('/career/hire/create'); }}
                >
                  <BriefcaseIcon />
                  <span> Application</span>
                </button>
              </div>
            )}
          </div>
        }
      </div>

      {/* ── Content ── */}
      <div className="scl--cms-pages-dashboard-content">

        {/* Section header */}

        {canAccessWorkflow &&
          <>
            <div className="scl--cms-pages-dashboard-section-header">
              <button className="scl--cms-pages-dashboard-section-title" onClick={() => navigate('/workflow')}>Workflow management</button>
              <button
                className="scl--cms-pages-dashboard-see-more"
                onClick={() => navigate('/workflow')}
              >See more</button>
            </div>

            {/* Stat cards row 1 */}
            <div className="scl--cms-pages-dashboard-cards-row">
              {statCards.slice(0, 3).map((card) => (
                <WorkflowCard
                  key={card.id}
                  label={card.label}
                  value={card.value}
                  color={card.color}
                  gradient={card.gradient}
                  borderColor={card.borderColor}
                  iconSrc={card.iconSrc}
                  iconAlt={card.iconAlt}
                />
              ))}
            </div>

            {/* Stat cards row 2 */}
            <div className="scl--cms-pages-dashboard-cards-row">
              {statCards.slice(3).map((card) => (
                <WorkflowCard
                  key={card.id}
                  label={card.label}
                  value={card.value}
                  color={card.color}
                  gradient={card.gradient}
                  borderColor={card.borderColor}
                  iconSrc={card.iconSrc}
                  iconAlt={card.iconAlt}
                />
              ))}
            </div>
          </>
        }

        {/* Charts row */}
        {(canAccessCareer || canAccessMedia) &&
          <div className="scl--cms-pages-dashboard-charts-row">
            {canAccessCareer &&
              <CareerChart
                title="Career"
                bars={barData}
                yStep={10}
                onTitleClick={() => navigate('/career/hire')}
                onMenuClick={() => console.log('Career menu clicked')}
              />
            }

            {canAccessMedia &&
              <MediaChart
                title="Media"
                totalLabel="Total"
                subLabel="Media file"
                legend={mediaLegend}
                stats={mediaStats}
                footerLabel={MEDIA_FOOTER_LINK.label}
                onFooterClick={() => navigate(MEDIA_FOOTER_LINK.href)}
                onOpenClick={() => navigate('/media')}
                onMenuClick={() => console.log('Media menu clicked')}
              />
            }

          </div>
        }

        {/* Bottom row */}
        {(canAccessInbox || canAccessNotification) &&
          <div className="scl--cms-pages-dashboard-bottom-row">
            {canAccessInbox &&
              <InboxChart
                title="Inbox"
                totalLabel="Total"
                subLabel="inbox"
                legend={inboxLegend}
                footerLabel={INBOX_FOOTER_LINK.label}
                onFooterClick={() => navigate('/inbox')}
                onOpenClick={() => navigate('/inbox')}
                onTitleClick={() => navigate('/inbox')}
                onMenuClick={() => console.log('Inbox menu clicked')}
              />
            }

            {canAccessNotification &&
              <NotificationTable
                title="Notification"
                onOpenClick={() => navigate('/notification')}
                onSeeMore={() => navigate('/notification')}
              />
            }
          </div>
        }

      </div>
    </div>
  );
};

export default DashboardPage;